import React, { useEffect, useRef, useState } from 'react';
import { CatalogImage, CatalogImageProps } from '../../Wysiwyg/CatalogImage';
import { catalogImageId } from '../../Wysiwyg/catalogImages';
import { attachmentIdForSource, forgetThumbnail, resolveThumbnail } from './thumbnailResolver';

/**
 * Renders an inline image inside a comment body.
 *
 * The activity pane builds these into a markdown string pointing at the attachment's stored
 * SharePoint link, which expires - so the markdown renderer would otherwise paint the browser's
 * broken glyph. Resolve the link first and render nothing until it is known to work; if there
 * is no usable preview, fall back to the file name so the surrounding link is still useful.
 */
export const InlineAttachmentImage = (props: CatalogImageProps) =>
  catalogImageId(props.src) ? <CatalogImage {...props} /> : <AttachmentImage {...props} />;

// A knowledge answer's picture reaches a comment when a deflected answer is written into the
// ticket, so comments have to resolve both kinds.
const AttachmentImage = (props: CatalogImageProps) => {
  const { node, src, alt, ticketId, ...rest } = props;
  const [resolved, setResolved] = useState<string | null | undefined>(undefined);
  const retried = useRef(false);

  // A remembered link is handed back without re-verifying, so it can be one that has expired
  // since. Forget it and resolve once more - that path verifies and refreshes - rather than
  // leaving the dead entry to fail again on every later visit.
  const onImageError = async () => {
    const attachmentId = attachmentIdForSource(src);
    if (retried.current || !attachmentId) {
      setResolved(null);
      return;
    }
    retried.current = true;
    forgetThumbnail(attachmentId);
    setResolved(undefined);

    try {
      setResolved(await resolveThumbnail({ Id: attachmentId, ThumbnailUrl: src }));
    } catch {
      setResolved(null);
    }
  };

  useEffect(() => {
    let cancelled = false;
    retried.current = false;
    const attachmentId = attachmentIdForSource(src);

    // Not one of ours (a pasted external image, say) - leave it alone.
    if (!attachmentId) {
      setResolved(src ?? null);
      return;
    }

    setResolved(undefined);
    resolveThumbnail({ Id: attachmentId, ThumbnailUrl: src })
      .then(url => { if (!cancelled) setResolved(url); })
      .catch(() => { if (!cancelled) setResolved(null); });

    return () => { cancelled = true; };
  }, [src]);

  if (resolved === undefined)
    return null;

  if (!resolved)
    return <>{(alt ?? '').replace(/\s*-tikitimg$/, '')}</>;

  return <img src={resolved} alt={alt} onError={onImageError} {...rest} />;
};
